import { NavLink } from "react-router";
import { useFavoritesCount } from "@/entities/favorite";
import { useCartCount } from "@/entities/cart";
import { useSettings } from "@/entities/settings";
import { useT } from "@/shared/i18n";
import type { JSX, ReactNode } from "react";
import { Home, Heart, ShoppingCart, User } from "lucide-react";

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  badgeCount: number;
}

const ICON_PROPS = { size: 22, strokeWidth: 1.8 } as const;
const HomeIcon = <Home {...ICON_PROPS} aria-hidden="true" />;
const HeartIcon = <Heart {...ICON_PROPS} aria-hidden="true" />;
const CartIcon = <ShoppingCart {...ICON_PROPS} aria-hidden="true" />;
const UserIcon = <User {...ICON_PROPS} aria-hidden="true" />;

export function ResponsiveNav(): JSX.Element {
  const { t } = useT();
  const favoritesCount = useFavoritesCount();
  const cartCount = useCartCount();
  const { get } = useSettings();
  const appName = String(get("app_name"));

  const items: ReadonlyArray<NavItem> = [
    { to: "/", label: t("nav.home"), icon: HomeIcon, badgeCount: 0 },
    { to: "/favorites", label: t("nav.favorites"), icon: HeartIcon, badgeCount: favoritesCount },
    { to: "/cart", label: t("nav.cart"), icon: CartIcon, badgeCount: cartCount },
    { to: "/profile", label: t("nav.profile"), icon: UserIcon, badgeCount: 0 },
  ];

  return (
    <>
      <nav className="bottom-nav" aria-label={t("nav.home")}>
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.to === "/"}
            className={({ isActive }) => (isActive ? "active" : "")}
          >
            {it.icon}
            <span>{it.label}</span>
            {it.badgeCount > 0 ? <span className="badge">{it.badgeCount}</span> : null}
          </NavLink>
        ))}
      </nav>
      <aside className="side-nav">
        <div className="side-nav-brand">{appName}</div>
        <nav className="side-nav-items" aria-label={t("nav.home")}>
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/"}
              className={({ isActive }) => (isActive ? "side-nav-link active" : "side-nav-link")}
            >
              <span className="side-nav-icon">{it.icon}</span>
              <span className="side-nav-label">{it.label}</span>
              {it.badgeCount > 0 ? <span className="badge">{it.badgeCount}</span> : null}
            </NavLink>
          ))}
        </nav>
      </aside>
    </>
  );
}
